import { styled } from '@mui/material/styles';

import type { Opportunity } from '../../shared/opportunity';
import { BRAND } from '../../shared/theme/paletteDark';
import { FONTS } from '../../shared/theme/typography';
import type { RsvpStatus } from '../../shared/useRsvp';
import { inMonth, monthGrid, type Month } from './month';

/**
 * The phone's month: seven columns of day numbers, a dot under each for its
 * events (Going solid, Maybe at half strength, one on neither list a ring;
 * three at most, the user's own first). Tapping a day lists it below.
 */

const MAX_DOTS = 3;
const HEADS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

const Grid = styled('div')({
  display: 'grid',
  gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
  rowGap: 2,
});

const Head = styled('div')({
  textAlign: 'center',
  padding: '4px 0 6px',
  fontFamily: FONTS.body,
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.1em',
  color: 'rgba(243,238,231,0.5)',
});

const Day = styled('button', { shouldForwardProp: (p) => p !== '$outside' })<{ $outside: boolean }>(
  ({ $outside }) => ({
    appearance: 'none',
    border: 0,
    background: 'transparent',
    padding: '3px 0 5px',
    height: 48,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 4,
    color: BRAND.parchment,
    opacity: $outside ? 0.35 : 1,
    cursor: 'pointer',
    WebkitTapHighlightColor: 'transparent',
    '&:focus-visible': { outline: `2px solid ${BRAND.parchment}`, outlineOffset: -2, borderRadius: 10 },
  }),
);

const DayNum = styled('span', { shouldForwardProp: (p) => p !== '$selected' && p !== '$today' && p !== '$past' })<{
  $selected: boolean;
  $today: boolean;
  $past: boolean;
}>(({ $selected, $today, $past }) => ({
  width: 30,
  height: 30,
  display: 'grid',
  placeItems: 'center',
  borderRadius: 999,
  fontFamily: FONTS.body,
  fontSize: 15,
  fontWeight: $selected || $today ? 700 : 500,
  fontVariantNumeric: 'tabular-nums',
  background: $selected ? BRAND.parchment : 'transparent',
  color: $selected ? BRAND.mocha : BRAND.parchment,
  boxShadow: $today && !$selected ? `inset 0 0 0 1.5px ${BRAND.parchment}` : 'none',
  opacity: $past && !$selected && !$today ? 0.6 : 1,
  transition: 'background 160ms ease, color 160ms ease',
}));

const Dots = styled('span')({ display: 'flex', gap: 3, height: 5 });

const Dot = styled('span', { shouldForwardProp: (p) => p !== '$tone' })<{ $tone: RsvpStatus | 'plain' }>(
  ({ $tone }) => ({
    width: 5,
    height: 5,
    boxSizing: 'border-box',
    borderRadius: 999,
    background: $tone === 'plain' ? 'transparent' : BRAND.parchment,
    boxShadow: $tone === 'plain' ? `inset 0 0 0 1px ${BRAND.parchment}` : 'none',
    opacity: $tone === 'maybe' ? 0.45 : $tone === 'plain' ? 0.75 : 1,
  }),
);

const RANK = { going: 0, maybe: 1, plain: 2 } as const;

interface Props {
  month: Month;
  today: string;
  days: ReadonlyMap<string, Opportunity[]>;
  statusOf: (id: string) => RsvpStatus | null;
  selected: string;
  onSelect: (day: string) => void;
}

export default function DotCalendar({ month, today, days, statusOf, selected, onSelect }: Props) {
  return (
    <Grid>
      {HEADS.map((d, i) => (
        <Head key={i} aria-hidden="true">
          {d}
        </Head>
      ))}
      {monthGrid(month.year, month.month).flat().map((day) => {
        const events = days.get(day) ?? [];
        const date = Number(day.slice(8));
        const tones = events.map((e) => statusOf(e.id) ?? 'plain').sort((a, b) => RANK[a] - RANK[b]);
        return (
          <Day
            key={day}
            type="button"
            $outside={!inMonth(day, month)}
            aria-pressed={day === selected}
            aria-label={`${new Date(`${day}T12:00:00Z`).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', timeZone: 'UTC' })}${day === today ? ', today' : ''}${events.length ? `, ${events.length} ${events.length === 1 ? 'event' : 'events'}` : ''}`}
            onClick={() => onSelect(day)}
          >
            <DayNum $selected={day === selected} $today={day === today} $past={day < today}>
              {date}
            </DayNum>
            <Dots aria-hidden="true">
              {tones.slice(0, MAX_DOTS).map((tone, i) => (
                // Going's dots first, so a day of both reads solid then faint.
                <Dot key={i} $tone={tone} />
              ))}
            </Dots>
          </Day>
        );
      })}
    </Grid>
  );
}
